import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { Alert, useTheme } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Player } from '@lottiefiles/react-lottie-player';
import Card from '@mui/material/Card';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar, BaseLabel } from '@mgm/common/components/base';
import { useGetStaffAPI, useSetAttributeAPI } from '../../../hooks/api/POS/useOrders';
import { useQueryParams } from '../../../hooks/useCommon';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getIsDisplaySuccessAnim, getStaff } from '../../../store/pos/selectors';
import { TPersonnel } from '../../../types';
import { ANIMATIONS } from '../../../utils/enums';
import { posActions } from '../../../store/pos/slice';

const WhoHelped = () => {
  const query = useQueryParams();

  useGetStaffAPI(query.get('shopName') || '', query.get('locationId'));
  const {
    mutateAsync,
    isLoading,
    isError,
    reset,
  } = useSetAttributeAPI();

  const theme = useTheme();
  const members = useAppSelector(getStaff);
  const isDisplayAnim = useAppSelector(getIsDisplaySuccessAnim);
  const dispatch = useAppDispatch();
  const { pageHeight, resizableDiv } = useScreenResize(30);
  const [selectedMember, setSelectedMember] = useState({} as Partial<TPersonnel>);

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        reset();
      }, 4000);
    }
  }, [isError]);

  const selectMember = (member: Partial<TPersonnel>) => {
    setSelectedMember(member);
  };

  const setAttribute = async () => {
    const data = {
      shopName: query.get('shopName') || '',
      userId: query.get('userId') || '',
      orderId: query.get('orderId') || '',
      email: selectedMember.email || '',
      locationId: query.get('locationId') || '',
    };

    await mutateAsync(data);
    dispatch(posActions.setIsDisplaySuccessAnim(true));
  };

  return (
    <Box pb={4}>
      {isError && (
      <Alert severity="error">
        Oops! It seems there was an error while attributing the sale. Please try again or contact support for assistance.
      </Alert>
      )}
      <div ref={resizableDiv}>
        {!isDisplayAnim ? (
          <Box
            maxHeight={pageHeight}
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h4" pb={2}>Attribute sale to staff</Typography>
            <Grid
              container
              spacing={3}
            >
              {members.map((member) => (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                    lg: 4,
                  }}
                  px={2}
                  key={member.email}
                >
                  <Card
                    onClick={() => selectMember(member)}
                    className="align-center cursor-pointer"
                    component={Box}
                    sx={{
                      ...(member.email === selectedMember.email && {
                        border: `1px solid ${theme.palette.primary.main}`,
                      }),
                      px: 2,
                      width: 1,
                      position: 'relative',
                    }}
                  >
                    <MemoizedBaseAvatar
                      avatarName={member.name as string}
                      avatarFile={member.avatarFile as string | null}
                    />
                    <Box pl={1} py={3}>
                      <Typography
                        variant="body1"
                        sx={{
                          transition: (theme) => theme.transitions.create('opacity', {
                            duration: theme.transitions.duration.shortest,
                          }),
                        }}
                      >
                        {member.name}
                      </Typography>
                      <BaseLabel letterSpacing="-0.5px">{member.position}</BaseLabel>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box pb={2} display="flex" justifyContent="flex-end" mt={4}>
              <LoadingButton
                loading={isLoading}
                onClick={setAttribute}
                variant="contained"
                disabled={!selectedMember.email}
              >
                Set member
              </LoadingButton>
            </Box>
          </Box>
        )
          : (
            <Box
              sx={{
                transform: 'translate(-50%, -50%)',
              }}
              position="fixed"
              top="50%"
              left="50%"
            >
              <Player
                src={ANIMATIONS.SUCCESS}
                keepLastFrame
                autoplay
                style={{
                  width: 180,
                }}
              />
              <Typography width={300} textAlign="center">
                {`Success! The sale has been attributed to ${selectedMember.name}. Please press 'Close' to exit this window.`}
              </Typography>
            </Box>
          )}
      </div>
    </Box>
  );
};

export {
  WhoHelped,
};
